import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useState } from "react";
import { contactUs } from "../../api/parentApi";
import SendIcon from "@mui/icons-material/Send";

const ContactForm = () => {
  const storedParent = localStorage.getItem("parentInfo");
  const isParentLoggedIn = !!storedParent;

  const initialMessage = isParentLoggedIn
    ? "We're here to help you and your child make the most of MySchoolling 😊 \n\nFor any questions about homework support, app usage, or your child's progress, feel free to reach out to us anytime."
    : "Hi, can you please give us a short demo? I'd like to see how it works.";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: initialMessage,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = "Enter a valid email address";
    if (!/^\+?\d{10,13}$/.test(formData.phone)) newErrors.phone = "Enter a valid phone number";
    if (formData.message.trim().length < 20) newErrors.message = "Message must be at least 20 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: undefined });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    if (!validateForm()) return;
    setLoading(true);
    try {
      await contactUs(formData);
      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", message: initialMessage });
      setErrors({});
    } catch (error) {
      setErrors({ submit: `Failed: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#fff",
      borderRadius: "14px",
      "& fieldset": { borderColor: "rgba(13,27,75,0.1)", borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: "rgba(79,110,247,0.4)" },
      "&.Mui-focused fieldset": { borderColor: "#4F6EF7", borderWidth: "2px" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#4F6EF7" },
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography
        variant="h4"
        fontWeight={800}
        color="#0D1B4B"
        mb={1}
        sx={{ fontSize: { xs: "1.5rem", md: "1.9rem" } }}
      >
        Send Us a Message
      </Typography>
      <Typography variant="body2" color="#64748B" mb={4} lineHeight={1.7}>
        We typically respond within a few hours during business hours.
      </Typography>

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: "14px", fontWeight: 500 }}
        >
          🎉 Your message has been sent! We'll get back to you shortly.
        </Alert>
      )}

      {errors.submit && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: "14px" }}>
          {errors.submit}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Full Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            error={!!errors.phone}
            helperText={errors.phone}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Message"
            name="message"
            multiline
            rows={5}
            value={formData.message}
            onChange={handleChange}
            error={!!errors.message}
            helperText={errors.message}
            sx={fieldSx}
          />
        </Grid>
      </Grid>

      <Button
        fullWidth
        variant="contained"
        size="large"
        type="submit"
        disabled={loading}
        endIcon={!loading && <SendIcon />}
        sx={{
          mt: 4,
          py: 1.8,
          borderRadius: "50px",
          fontWeight: 700,
          fontSize: "1rem",
          background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
          boxShadow: "0 12px 32px rgba(79,110,247,0.35)",
          "&:hover": {
            background: "linear-gradient(135deg, #0D1B4B 0%, #3A5AE8 100%)",
            boxShadow: "0 16px 40px rgba(79,110,247,0.45)",
          },
        }}
      >
        {loading ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          isParentLoggedIn ? "Get Help" : "Book a Demo"
        )}
      </Button>
    </Box>
  );
};

export default ContactForm;
